import { useState, useEffect, useCallback } from 'react'
import type { Model } from '../lib/api'
import { haptics } from '../lib/haptics'

/**
 * Step-by-step guide: how to connect a model to your app/agent.
 *
 * 3 slides — only the essentials:
 *   1. Get your API key  (Home → Integration)
 *   2. Set base URL      (imago.market/v1 — OpenAI-compatible)
 *   3. Use the model ID  (copy the exact string, paste as "model" param)
 *
 * TODO: Replace the emoji placeholders in each slide with a short GIF/video
 * showing the actual Imago interface performing that step. Each slide has
 * a `mediaSlot` reserved for this purpose (see comment below).
 */

const SLIDE_DURATION = 7000

interface Slide {
  /** Emoji placeholder — replace with <img src={gifUrl} /> or <video> */
  icon: string
  accentHex: string
  step: string
  title: string
  body: string
  /** Inline code value to copy (optional) */
  code?: string
  codeCopy?: string
  codeLabel?: string
  codeNote?: string
}

function buildSlides(model: Model): Slide[] {
  return [
    {
      icon: '🔑',
      accentHex: '#FF9800',
      step: 'Step 1 of 3',
      title: 'Get your API key',
      body: 'Open the Home tab → find the Integration section → copy your personal API key. You\'ll need it for every request.',
      // TODO: replace icon with GIF: Home → Integration section → Copy button tap
    },
    {
      icon: '🔌',
      accentHex: '#2196F3',
      step: 'Step 2 of 3',
      title: 'Point to Imago API',
      body: 'Use any OpenAI-compatible SDK. Set the base URL to the Imago endpoint — no other config needed.',
      code: 'https://imago.market/v1',
      codeCopy: 'https://imago.market/v1',
      codeLabel: 'Copy URL',
      // TODO: replace icon with GIF: code editor changing baseURL
    },
    {
      icon: '⎘',
      accentHex: '#00BCD4',
      step: 'Step 3 of 3',
      title: 'Use this model ID',
      body: 'Pass the exact string below as the "model" parameter in your API call. Done — you\'re making calls to this model.',
      code: model.id,
      codeCopy: model.id,
      codeLabel: 'Copy ID',
      codeNote: 'model identifier',
      // TODO: replace icon with GIF: model.id being pasted into code, request firing
    },
  ]
}

interface Props {
  model: Model
  onDone: () => void
}

export default function ModelOnboardingStories({ model, onDone }: Props) {
  const slides = buildSlides(model)
  const [current, setCurrent] = useState(0)
  const [progress, setProgress] = useState(0)
  const [copied, setCopied] = useState(false)

  const advance = useCallback(() => {
    if (current < slides.length - 1) {
      haptics.selection()
      setCurrent((c) => c + 1)
      setProgress(0)
      setCopied(false)
    } else {
      haptics.impact('light')
      onDone()
    }
  }, [current, slides.length, onDone])

  const goBack = useCallback(() => {
    if (current > 0) {
      haptics.selection()
      setCurrent((c) => c - 1)
      setProgress(0)
      setCopied(false)
    }
  }, [current])

  useEffect(() => {
    setProgress(0)
    const startTime = Date.now()
    let raf: number

    const tick = () => {
      const elapsed = Date.now() - startTime
      const pct = Math.min((elapsed / SLIDE_DURATION) * 100, 100)
      setProgress(pct)
      if (pct < 100) {
        raf = requestAnimationFrame(tick)
      } else {
        advance()
      }
    }

    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [current, advance])

  const slide = slides[current]
  const isLast = current === slides.length - 1

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!slide.codeCopy) return
    try {
      await navigator.clipboard.writeText(slide.codeCopy)
      haptics.notification('success')
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      haptics.notification('error')
    }
  }

  return (
    <div
      className="flex flex-col"
      style={{ backgroundColor: 'var(--tg-theme-bg-color)', minHeight: '460px' }}
    >
      {/* Progress bars */}
      <div className="flex gap-1.5 px-sp-4 pt-sp-3 pb-sp-1">
        {slides.map((_, i) => (
          <div
            key={i}
            className="flex-1 rounded-full overflow-hidden"
            style={{ height: '2px', backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}
          >
            <div
              className="h-full rounded-full"
              style={{
                width: i < current ? '100%' : i === current ? `${progress}%` : '0%',
                backgroundColor: 'var(--tg-theme-button-color)',
              }}
            />
          </div>
        ))}
      </div>

      {/* Tap zones */}
      <div className="relative flex flex-1" style={{ minHeight: '380px' }}>
        <div className="absolute left-0 top-0 bottom-0 z-10" style={{ width: '30%' }} onClick={goBack} />
        <div className="absolute right-0 top-0 bottom-0 z-10" style={{ width: '70%' }} onClick={advance} />

        <div className="flex flex-col flex-1 px-sp-4 pb-sp-4">

          {/* Media slot — currently an emoji badge, replace with GIF per slide */}
          {/* TODO: <img className="w-full rounded-[16px] mb-sp-3" src={slide.gifUrl} /> */}
          <div
            className="w-[56px] h-[56px] rounded-[18px] flex items-center justify-center text-[26px] mt-sp-3 mb-sp-3"
            style={{
              backgroundColor: `${slide.accentHex}18`,
              border: `1.5px solid ${slide.accentHex}30`,
            }}
          >
            {slide.icon}
          </div>

          {/* Step pill */}
          <div
            className="self-start px-sp-2 py-[3px] rounded-full mb-sp-2"
            style={{ backgroundColor: `${slide.accentHex}15` }}
          >
            <span className="text-[11px] font-semibold" style={{ color: slide.accentHex }}>
              {slide.step}
            </span>
          </div>

          {/* Title */}
          <h2
            className="text-[21px] font-bold leading-snug mb-sp-2"
            style={{ color: 'var(--tg-theme-text-color)' }}
          >
            {slide.title}
          </h2>

          {/* Body */}
          <p
            className="text-[14px] leading-relaxed"
            style={{ color: 'var(--tg-theme-hint-color)' }}
          >
            {slide.body}
          </p>

          {/* Code block */}
          {slide.code && (
            <div
              className="relative rounded-[12px] mt-sp-3 z-20 overflow-hidden"
              style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {slide.codeNote && (
                <p
                  className="px-sp-3 pt-sp-2 pb-0 text-[10px] font-semibold uppercase tracking-wider"
                  style={{ color: 'var(--tg-theme-hint-color)' }}
                >
                  {slide.codeNote}
                </p>
              )}
              <div className="flex items-center justify-between px-sp-3 py-sp-2">
                <code
                  className="text-[13px] font-mono truncate flex-1"
                  style={{ color: 'var(--tg-theme-text-color)' }}
                >
                  {slide.code}
                </code>
                <button
                  onClick={handleCopy}
                  className="shrink-0 ml-sp-3 text-[12px] font-semibold active:opacity-70 transition-opacity"
                  style={{
                    color: copied
                      ? 'var(--tg-theme-hint-color)'
                      : 'var(--tg-theme-accent-text-color)',
                  }}
                >
                  {copied ? '✓ Copied' : slide.codeLabel}
                </button>
              </div>
            </div>
          )}

          <div className="flex-1" />

          {/* Navigation */}
          <div
            className="flex items-center justify-between z-20 mt-sp-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={goBack}
              className="text-[14px] font-medium active:opacity-60 transition-opacity"
              style={{
                color: current === 0 ? 'transparent' : 'var(--tg-theme-hint-color)',
                pointerEvents: current === 0 ? 'none' : 'auto',
              }}
            >
              ← Back
            </button>

            <button
              onClick={advance}
              className="px-6 py-[10px] rounded-[13px] text-[15px] font-semibold active:opacity-75 transition-opacity"
              style={{
                backgroundColor: 'var(--tg-theme-button-color)',
                color: 'var(--tg-theme-button-text-color)',
              }}
            >
              {isLast ? 'Got it ✓' : 'Next →'}
            </button>
          </div>

        </div>
      </div>
    </div>
  )
}
